import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import type { InventoryItemView, InventoryStatusEnum } from '@/lib/api-admin/types';
import { DataTable, StatusBadge, RowActions, Button, type DataTableColumn } from '@/components/ui';
import { InventoryAdjustExpand } from './InventoryAdjustExpand';
import { useInventoryLocationsQuery } from '../hooks/useInventoryLocationsQuery';

type InventoryItemViewExtended = InventoryItemView & {
  productName?: string | null;
  thumbnailUrl?: string | null;
  optionLabels?: string[] | null;
};

interface Props {
  items: ReadonlyArray<InventoryItemViewExtended>;
  isLoading?: boolean;
  onMoveClick?: (item: InventoryItemViewExtended) => void;
}

const STATUS_VARIANT: Record<InventoryStatusEnum, 'success' | 'warning' | 'danger'> = {
  NORMAL: 'success',
  LOW: 'warning',
  OUT_OF_STOCK: 'danger',
};

export const InventoryTable = ({ items, isLoading, onMoveClick }: Props) => {
  const { t } = useTranslation('inventory');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string | number>>(new Set());
  const locationsQuery = useInventoryLocationsQuery();
  const locations = locationsQuery.data ?? [];

  const locationColumns: DataTableColumn<InventoryItemViewExtended>[] = locations.map((loc) => ({
    key: `loc-${loc.locationId}`,
    header: loc.name,
    align: 'right' as const,
    cell: (row) => (
      <span className="text-sm">{row.qtyByLocation[loc.code] ?? '-'}</span>
    ),
  }));

  const columns: DataTableColumn<InventoryItemViewExtended>[] = [
    {
      key: 'product',
      header: t('inventory.columns.product'),
      cell: (row) => {
        const displayName = row.productName ?? t('inventory.table.unnamedProduct');
        return (
          <div className="flex items-center gap-2">
            {row.thumbnailUrl ? (
              <img
                src={row.thumbnailUrl}
                alt=""
                width={32}
                height={32}
                className="rounded object-cover flex-shrink-0"
                style={{ width: 32, height: 32 }}
              />
            ) : (
              <div
                className="rounded flex-shrink-0"
                style={{ width: 32, height: 32, background: 'var(--surface-1)' }}
              />
            )}
            <div className="max-w-[180px]">
              <div className="truncate text-sm font-medium" style={{ color: 'var(--content-strong)' }} title={displayName}>
                {displayName}
              </div>
              {row.sku ? (
                <div className="truncate text-xs" style={{ color: 'var(--content-muted)' }}>{row.sku}</div>
              ) : null}
            </div>
          </div>
        );
      },
    },
    ...locationColumns,
    {
      key: 'total',
      header: t('inventory.columns.total'),
      align: 'right',
      cell: (row) => <span className="text-sm font-semibold">{row.totalQty}</span>,
    },
    {
      key: 'threshold',
      header: t('inventory.columns.threshold'),
      align: 'right',
      cell: (row) => <span className="text-sm">{row.threshold}</span>,
    },
    {
      key: 'backorder',
      header: t('inventory.columns.backorder'),
      align: 'center',
      cell: (row) => (
        <span className="text-xs" style={{ color: 'var(--content-muted)' }}>
          {row.backorderAllowed ? t('inventory.backorder.yes') : t('inventory.backorder.no')}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('inventory.columns.status'),
      cell: (row) => (
        <StatusBadge
          variant={STATUS_VARIANT[row.status]}
          label={t(`inventory.status.${row.status}`)}
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <RowActions>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExpandedId(expandedId === row.inventoryId ? null : row.inventoryId)}
          >
            {t('inventory.actions.adjust')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onMoveClick?.(row)}
          >
            {t('inventory.actions.move')}
          </Button>
        </RowActions>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16" style={{ color: 'var(--content-muted)' }}>
        {t('inventory.table.loading')}
      </div>
    );
  }

  return (
    <DataTable
      columns={columns}
      rows={items as InventoryItemViewExtended[]}
      rowKey={(row) => row.inventoryId}
      selectable
      selectedKeys={selectedKeys}
      onSelectionChange={setSelectedKeys}
      expandable
      renderExpanded={(row) =>
        expandedId === row.inventoryId ? (
          <InventoryAdjustExpand item={row} onClose={() => setExpandedId(null)} />
        ) : null
      }
      emptyState={t('inventory.table.empty')}
      stickyHeader
    />
  );
};
